import endpoints from '@app/src/api/endpoints'
import { FetchKey, useFetchCollectionWithPost } from '@app/src/api/fetchHooks'
import EmptyState from '@app/src/components/EmptyState'
import Table from '@app/src/components/Table'
import { useAccount } from '@app/src/context/AccountContext'
import usePaginationWithQuery from '@app/src/hooks/paginationWithQuery'
import { useGetApiQueryFilters } from '@app/src/hooks/queryFilters'
import useSort from '@app/src/hooks/sorting'
import CustomQuestionHeader from '@app/src/pages/Configurations/SustainabilityLibraryPages/Questions/CustomQuestionHeader'
import CustomQuestionRow from '@app/src/pages/Configurations/SustainabilityLibraryPages/Questions/CustomQuestionRow'
import type { QuestionSelectionProps } from '@app/src/pages/Configurations/SustainabilityLibraryPages/Questions/StandardQuestionCollection'
import CustomQuestionFilters from '@app/src/pages/ResourceCollection/Filters/CustomQuestionFilters'
import { QUESTIONNAIRE_FILTER_NAME } from '@app/src/pages/ResourceCollection/Filters/QuestionnaireFilter'
import { Operators } from '@app/src/pages/ResourceCollection/Filters/useFilters'
import ResourceCollection from '@app/src/pages/ResourceCollection/index'
import type { Question } from '@app/src/types/resourceExplorer'
import { FormatListNumberedRtlOutlined } from '@mui/icons-material'
import { Box } from '@mui/material'
import type { FC } from 'react'
import React from 'react'
import { useIntl } from 'react-intl'

const allowedFilters: string[] = ['id', 'questionText', QUESTIONNAIRE_FILTER_NAME]

interface CustomQuestionCollectionProps {
  selectionProps?: QuestionSelectionProps
  withLeftPadding?: boolean
}

const CustomQuestionCollection: FC<CustomQuestionCollectionProps> = ({ selectionProps, withLeftPadding }) => {
  const leftPadding = withLeftPadding ? 4 : 0
  const { formatMessage } = useIntl()
  const { sorting, toggleSorting } = useSort()
  const userFilters = useGetApiQueryFilters(allowedFilters)
  const [page, pageSize, setPage, setPageSize, resetPage] = usePaginationWithQuery(undefined, [userFilters])
  const { account } = useAccount()

  const implicitFilters = [
    { name: 'creatorOrganizationId', filters: [{ operator: Operators.EqualTo, value: account?.organization?.id }] },
  ]
  const { items, count, isLoading, isFetching, isError } = useFetchCollectionWithPost<Question>({
    key: FetchKey.Questions,
    endpoint: endpoints.questionCollection,
    payload: {
      sort: sorting,
      include: ['sections.questionnaireTemplate'],
      filter: [...implicitFilters, ...userFilters],
      pagination: {
        pageNumber: page,
        itemsPerPage: pageSize,
      },
    },
  })

  const onCheckboxChange = (e: React.ChangeEvent<HTMLInputElement>, question: Question) => {
    if (e.target.checked) {
      selectionProps?.setSelectedQuestionIds(prev => [...prev, question.id])
    } else {
      selectionProps?.setSelectedQuestionIds(prev => prev.filter(questionId => questionId !== question.id))
    }
  }

  return (
    <ResourceCollection
      title=""
      embedded
      filter={
        <Box pl={leftPadding}>
          <CustomQuestionFilters
            allowedFilters={allowedFilters}
            resetPage={resetPage}
            implicitFilters={implicitFilters}
            showQuestionnaireFilter
          />
        </Box>
      }
    >
      <Box display="flex" flexDirection="column" flexGrow={1} minHeight={0} pl={leftPadding}>
        <Table<Question>
          RowComponent={({ row }) => (
            <CustomQuestionRow
              row={row}
              selectionProps={
                selectionProps && {
                  selectedQuestionIds: selectionProps.selectedQuestionIds,
                  onCheckboxChange: onCheckboxChange,
                  questionIdsAlreadyInQuestionnaire: selectionProps.questionIdsAlreadyInQuestionnaire,
                }
              }
            />
          )}
          HeaderComponent={() => (
            <CustomQuestionHeader
              toggleSorting={toggleSorting}
              activeSorting={sorting}
              includeCheckboxHeader={Boolean(selectionProps)}
              selectionProps={
                selectionProps && {
                  selectedQuestionIds: selectionProps.selectedQuestionIds,
                  setSelectedQuestionIds: selectionProps.setSelectedQuestionIds,
                  onCheckboxChange: onCheckboxChange,
                  questionIdsAlreadyInQuestionnaire: selectionProps.questionIdsAlreadyInQuestionnaire,
                }
              }
              questionsInPage={items}
            />
          )}
          emptyState={
            <EmptyState
              iconComponent={FormatListNumberedRtlOutlined}
              title={formatMessage({ id: 'questionsConfig.customEmptyState.title' })}
              description={formatMessage({ id: 'questionsConfig.customEmptyState.description' })}
              data-testid="empty-state"
            />
          }
          data={items}
          isLoading={isLoading || isFetching}
          count={count}
          isError={isError}
          page={page}
          pageSize={pageSize}
          setPage={setPage}
          setPageSize={setPageSize}
        />
      </Box>
    </ResourceCollection>
  )
}

export default CustomQuestionCollection
